import { useState, useEffect, useCallback } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Mail, MailCheck, MailX, Loader2, Send, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { formatDateTime } from "../../lib/formatters";

const NotificationMailboxCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiClient.get(`/oauth/m365-mail/status`);
      setStatus(res.data);
    } catch {
      setStatus({ connected: false, configured: false, missing_env: [] });
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const verify = async () => {
    setBusy(true);
    try {
      const res = await apiClient.post(`/oauth/m365-mail/verify`);
      toast.success(`Microsoft sign-in OK — ready to send as ${res.data.mailbox}`);
      if (res.data.secret?.error) toast.warning(res.data.secret.error);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally {
      setBusy(false);
      fetchStatus();
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const res = await apiClient.post(`/oauth/m365-mail/test`, null, { params: testTo ? { target_email: testTo } : {} });
      if (res.data.dry_run) toast.info(res.data.detail);
      else toast.success(`Test email sent to ${res.data.sent_to} from ${res.data.from}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test email failed");
    } finally {
      setBusy(false);
      fetchStatus();
    }
  };

  if (!status) return null;

  return (
    <Card className="border-slate-200" data-testid="m365-mail-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Notification Mailbox (Microsoft 365)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          All emails the app sends (alerts, reminders, applicant and onboarding messages, password resets) go out
          from the shared mailbox below through Microsoft 365. No sign-in is needed — the app authenticates with its
          own Azure credentials.
        </p>

        {status.configured ? (
          <>
            <div
              className={`flex items-start gap-3 p-3 rounded-lg border ${status.healthy ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}
              data-testid="m365-mail-connected"
            >
              {status.healthy
                ? <MailCheck className="w-5 h-5 text-green-600 mt-0.5" />
                : <MailX className="w-5 h-5 text-red-600 mt-0.5" />}
              <div className="text-sm">
                <p className={`font-medium ${status.healthy ? "text-green-800" : "text-red-800"}`}>
                  Sending as <span data-testid="m365-mail-mailbox">{status.mailbox}</span>
                </p>
                {status.dry_run && (
                  <p className="text-amber-700 font-medium" data-testid="m365-mail-dry-run">
                    DRY RUN — this environment logs emails instead of sending them (M365_MAIL_DRY_RUN=true)
                  </p>
                )}
                {status.last_sent_at && <p className="text-slate-700">Last sent: {formatDateTime(status.last_sent_at)}</p>}
                {status.secret_expires_at && (
                  <p className={status.secret_days_left <= 30 ? "text-red-700 font-medium" : "text-slate-600"} data-testid="m365-mail-secret-expiry">
                    Azure secret expires {new Date(status.secret_expires_at).toLocaleDateString()} ({status.secret_days_left} days)
                  </p>
                )}
                {status.last_error && <p className="text-red-600 mt-1" data-testid="m365-mail-last-error">Last error: {status.last_error}</p>}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="Send test to (defaults to your email)"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                className="sm:max-w-xs"
                data-testid="m365-mail-test-input"
              />
              <Button variant="outline" onClick={sendTest} disabled={busy} data-testid="m365-mail-test-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Test Email
              </Button>
              <Button variant="outline" onClick={verify} disabled={busy} data-testid="m365-mail-verify-btn">
                <ShieldCheck className="w-4 h-4 mr-2" />
                Check Connection
              </Button>
            </div>
          </>
        ) : (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="m365-mail-disconnected">
            <MailX className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Not configured — the app cannot send email.</p>
              {status.missing_env?.length > 0 && (
                <p className="mt-1" data-testid="m365-mail-missing-env">
                  Missing server settings: <code>{status.missing_env.join(", ")}</code>
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationMailboxCard;
